/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import config from '../config';
import {Events} from '@exc/shared';
import {HelpCenterApi, HelpCenterButtonConfig, HelpCenterOpenConfig} from '@adobe/exc-app/helpcenter';
import {messenger} from '../Messenger';

const scopes = ['helpCenter', 'helpCenterResource'];

interface CallbacksObject {
  [id: string]: (value?: any) => void;
}

const defaultOpenConfig: HelpCenterOpenConfig = {
  config: {
    subject: 'subject',
    type: 'CONTEXTUAL_FEEDBACK_SUBMISSION'
  },
  selectedTab: 'feedback'
};

const helpCenter = {
  close: () => {
    messenger.send(Events.OPEN_HELP_CENTER, undefined);
  },
  open: (config = defaultOpenConfig) => {
    messenger.send(Events.OPEN_HELP_CENTER, config);
  },
  setButton: (buttonConfig: HelpCenterButtonConfig | HelpCenterButtonConfig[] | null) => {
    let customButtons = config.get('customButtons') || [];
    let buttons: HelpCenterButtonConfig[] = !buttonConfig ? [] : Array.isArray(buttonConfig) ? buttonConfig : [buttonConfig];
    // Reset customButtons
    if (!buttonConfig || buttons.length === 0) {
      customButtons = customButtons.filter(button => scopes.indexOf(button.scope) === -1);
      config.set({customButtons});
      messenger.setCallback(Events.CUSTOM_HELP_BUTTON_CLICK, null);
      return;
    }
    // Add buttons
    let callbacks: CallbacksObject = {};
    buttons.forEach(({
      callback,
      id = 'create_analytics_support_ticket',
      label,
      scope
    }: HelpCenterButtonConfig) => {
      customButtons.unshift({
        id,
        label,
        scope: scope || 'helpCenter'
      });
      callback && (callbacks[id] = callback);
    });
    config.set({customButtons});
    messenger.setCallback(Events.CUSTOM_HELP_BUTTON_CLICK, callbacks);
  },
  setButtons: (buttons: HelpCenterButtonConfig[] | null) => helpCenter.setButton(buttons)
} as HelpCenterApi;

Object.defineProperty(helpCenter, 'config', {
  get: () => {
    return config.get('helpCenter');
  },
  set: helpCenter => {
    config.set({helpCenter});
  }
});

export default helpCenter;
